import {
  Box,
  Typography,
  Divider,
  Grid,
  TextField,
  Switch,
  Stack,
  Card,
  CardContent,
  CardHeader,
  Alert,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  RadioGroup,
  FormControlLabel,
  Radio,
  Skeleton,
  Tooltip,
  Avatar,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../components/AuthContext";
import { useInstitution } from "../components/InstitutionContext";
import IconOnlyChip from "../components/IconOnlyChip";
import OutlinedButton from "../components/OutlinedButton";
import FilledButton from "../components/FilledButton";
import ChangePasswordDialog from "../components/ChangePasswordDialog";
import AvatarCropDialog from "../components/AvatarCropDialog";
import ChangeEmailDialog from "../components/ChangeEmailDialog";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import api, {
  fetchAdminProgress,
  fetchInstitutionAdminStats,
  applyForAdmin,
  fetchInstitutionStats,
  uploadProfileAvatar,
  uploadCommunityAvatar,
  deleteAvatar,
  deleteCommunityAvatar,
  resendVerificationEmail,
} from "../services/api";
import { useColorScheme } from "@mui/material/styles";
import { Autocomplete } from "@mui/material";
import HomeRoundedIcon from "@mui/icons-material/HomeRounded";
import ClassRoundedIcon from "@mui/icons-material/ClassRounded";
import ForumRoundedIcon from "@mui/icons-material/ForumRounded";
import SchoolRoundedIcon from "@mui/icons-material/SchoolRounded";
import { DataGrid } from "@mui/x-data-grid";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import { green, grey } from "@mui/material/colors";
import PermissionsMatrix from "../components/PermissionsMatrix";
import TextOnlyChip from "../components/TextOnlyChip";
import { colorSchemes } from "../shared-theme/themePrimitives";

const RESEND_EXPIRY_KEY = "verificationResendExpiry";

const SettingsPage = () => {
  const { user, loading, setUser } = useAuth();
  const { setSelectedInstitution } = useInstitution();
  const navigate = useNavigate();
  const location = useLocation();
  const [data, setData] = useState({
    user: { username: "", email: "" },
    profile: {
      institution: "",
      community_username: "",
      role_courses: "",
      role_community: "",
      full_name: "",
      tutor_id: null,
      student_id: null,
      verified_institutions: [],
      email_verified: false,
    },
  });
  const [editing, setEditing] = useState({});
  const [editedValues, setEditedValues] = useState({});
  const [institutions, setInstitutions] = useState([]);
  const [useDefaultCommunityUsername, setUseDefaultCommunityUsername] =
    useState(true);
  const [communityUsernameError, setCommunityUsernameError] = useState("");
  const [communityUsernameHelper, setCommunityUsernameHelper] = useState("");
  const [communityUsernameAlert, setCommunityUsernameAlert] = useState("");
  const [coursePerms, setCoursePerms] = useState([]);
  const [communityPerms, setCommunityPerms] = useState([]);
  const [pendingInstitution, setPendingInstitution] = useState(null);
  const [confirmInstitutionOpen, setConfirmInstitutionOpen] = useState(false);
  const [institutionAlert, setInstitutionAlert] = useState("");

  const [adminProgress, setAdminProgress] = useState(null);
  const [adminCounts, setAdminCounts] = useState(null);
  const [instStats, setInstStats] = useState(null);
  const [applyDialog, setApplyDialog] = useState({ app: "", open: false });
  const [adminRejectAlert, setAdminRejectAlert] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const [changeEmailOpen, setChangeEmailOpen] = useState(false);
  const [avatarDialogOpen, setAvatarDialogOpen] = useState(false);
  const [communityAvatarDialogOpen, setCommunityAvatarDialogOpen] =
    useState(false);
  const [useAccountAvatar, setUseAccountAvatar] = useState(true);
  const [resendCooldown, setResendCooldown] = useState(0);

  const verified = data.profile.email_verified;

  const { mode, systemMode, colorScheme, setMode, setColorScheme } =
    useColorScheme();
  const currentScheme =
    colorScheme === "warm"
      ? "warm"
      : mode === "system"
        ? systemMode || "light"
        : mode;
  const [selectedScheme, setSelectedScheme] = useState(currentScheme);
  const [savedScheme, setSavedScheme] = useState(currentScheme);
  const [schemeChanged, setSchemeChanged] = useState(false);

  useEffect(() => {
    const expiry = parseInt(localStorage.getItem(RESEND_EXPIRY_KEY) || "0", 10);
    if (expiry) {
      const remaining = Math.ceil((expiry - Date.now()) / 1000);
      if (remaining > 0) setResendCooldown(remaining);
      else localStorage.removeItem(RESEND_EXPIRY_KEY);
    }
  }, []);

  useEffect(() => {
    if (!loading && !user) {
      navigate("/sign-in", { state: { from: location.pathname } });
    }
  }, [user, loading, navigate, location]);

  useEffect(() => {
    if (location.hash) {
      const el = document.querySelector(location.hash);
      if (el) {
        setTimeout(() => {
          el.scrollIntoView({ behavior: "smooth" });
        }, 0);
      }
    }
  }, [location]);

  // Fetch user-related data once authenticated
  useEffect(() => {
    if (!user) return;
    api.get("/users/profile/").then((res) => {
      setData(res.data);
      const isDefault =
        !res.data.profile.community_username ||
        res.data.profile.community_username === res.data.user.username;
      setUseDefaultCommunityUsername(isDefault);
      setUseAccountAvatar(res.data.profile.use_account_avatar);
    });
    api
      .get("/courses/institutions/", { params: { show_all: "true" } })
      .then((res) => setInstitutions(res.data));

    api.get("/users/permissions/custom/").then((res) => {
      const courseFiltered = res.data.filter((p) => p.app_label === "courses");
      const communityFiltered = res.data.filter(
        (p) => p.app_label === "community",
      );
      setCoursePerms(courseFiltered);
      setCommunityPerms(communityFiltered); // ✅ THIS LINE WAS MISSING
    });
  }, [user]);

  useEffect(() => {
    if (!user || !data.profile.institution) return;
    const instId =
      typeof data.profile.institution === "object"
        ? data.profile.institution.id
        : data.profile.institution;
    fetchAdminProgress().then(setAdminProgress);
    fetchInstitutionStats(instId).then(setInstStats);
    fetchInstitutionAdminStats(instId).then(setAdminCounts);
  }, [user, data.profile.institution]);

  useEffect(() => {
    if (!schemeChanged) {
      setSelectedScheme(currentScheme);
      setSavedScheme(currentScheme);
    }
  }, [currentScheme, schemeChanged]);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const id = setInterval(() => {
      setResendCooldown((s) => {
        const next = s > 0 ? s - 1 : 0;
        if (next === 0) localStorage.removeItem(RESEND_EXPIRY_KEY);
        return next;
      });
    }, 1000);
    return () => clearInterval(id);
  }, [resendCooldown]);

  const startEditing = (field) => {
    setEditing((prev) => ({ ...prev, [field]: true }));
    setEditedValues((prev) => ({
      ...prev,
      [field]: data.profile[field] || data.user[field],
    }));
  };

  const handleChange = (field, value) => {
    setEditedValues((prev) => ({ ...prev, [field]: value }));
  };

  const saveField = async (field, value) => {
    const val = value !== undefined ? value : editedValues[field];
    const payload =
      field in data.user
        ? { user: { [field]: val } }
        : { profile: { [field]: val } };
    await api.put("/users/profile/", payload);
    const updated = await api.get("/users/profile/");
    setData(updated.data);
    if (!["community_username", "institution"].includes(field)) {
      setSuccessMessage("Changes saved.");
    }

    if (field === "institution") {
      setSelectedInstitution(
        updated.data.profile.institution
          ? String(updated.data.profile.institution.id)
          : "",
      );
      try {
        const resp = await api.get("/users/check/");
        if (resp.data.isAuthenticated) {
          setUser(resp.data.user);
        }
      } catch (err) {
        setErrorMessage("Could not refresh user info.");
      }
    }

    setEditing((prev) => ({ ...prev, [field]: false }));
  };

  const confirmInstitutionChange = async () => {
    if (!pendingInstitution) return;
    const oldName = data.profile.institution
      ? typeof data.profile.institution === "object"
        ? data.profile.institution.display_name
        : institutions.find((i) => i.id === data.profile.institution)
            ?.display_name || ""
      : "";
    handleChange("institution", pendingInstitution.id);
    await saveField("institution", pendingInstitution.id);
    setInstitutionAlert(
      `Institution changed from ${oldName} to ${pendingInstitution.display_name}`,
    );
    setPendingInstitution(null);
    setConfirmInstitutionOpen(false);
  };

  const handleThemeSave = () => {
    if (selectedScheme === "system") {
      setMode("system");
      setColorScheme("light");
    } else if (selectedScheme === "warm") {
      setColorScheme("warm");
    } else {
      setMode(selectedScheme);
      setColorScheme(selectedScheme);
    }
    setSavedScheme(selectedScheme);
    setSchemeChanged(false);
    setSuccessMessage("Theme updated.");
  };

  const handleThemeCancel = () => {
    setSelectedScheme(savedScheme);
    setSchemeChanged(false);
    if (savedScheme === "system") {
      setMode("system");
      setColorScheme("light");
    } else if (savedScheme === "warm") {
      setColorScheme("warm");
    } else {
      setMode(savedScheme);
      setColorScheme(savedScheme);
    }
  };

  const handleAvatarSave = async (blob) => {
    try {
      await uploadProfileAvatar(blob);
      const updated = await api.get("/users/profile/");
      setData(updated.data);
      setSuccessMessage("Avatar updated.");
    } catch (err) {
      setErrorMessage("Could not update avatar.");
    }
  };

  const handleAvatarDelete = async () => {
    try {
      await deleteAvatar();
      const updated = await api.get("/users/profile/");
      setData(updated.data);
      setSuccessMessage("Avatar deleted.");
    } catch (err) {
      setErrorMessage("Could not delete avatar.");
    }
  };

  const handleCommunityAvatarSave = async (blob) => {
    try {
      await uploadCommunityAvatar(blob);
      const updated = await api.get("/users/profile/");
      setData(updated.data);
      setSuccessMessage("Community avatar updated.");
    } catch (err) {
      setErrorMessage("Could not update community avatar.");
    }
  };

  const handleCommunityAvatarDelete = async () => {
    try {
      await deleteCommunityAvatar();
      const updated = await api.get("/users/profile/");
      setData(updated.data);
      setSuccessMessage("Community avatar deleted.");
    } catch (err) {
      setErrorMessage("Could not delete community avatar.");
    }
  };

  const updateUseAccountAvatar = async (checked) => {
    setUseAccountAvatar(checked);
    try {
      await api.put("/users/profile/", {
        profile: { use_account_avatar: checked },
      });
      const updated = await api.get("/users/profile/");
      setData(updated.data);
      setSuccessMessage("Avatar preference updated.");
    } catch (err) {
      setErrorMessage("Could not update avatar preference.");
    }
  };

  const refreshUserInfo = async () => {
    try {
      const updated = await api.get("/users/profile/");
      setData(updated.data);
    } catch (err) {
      setErrorMessage("Could not refresh user info.");
    }
    try {
      const resp = await api.get("/users/check/");
      if (resp.data.isAuthenticated) {
        setUser(resp.data.user);
      }
    } catch (err) {
      // ignore
    }
  };

  const handleResendVerification = async () => {
    try {
      const res = await resendVerificationEmail();
      if (res?.retry_after) {
        setResendCooldown(res.retry_after);
        localStorage.setItem(
          RESEND_EXPIRY_KEY,
          Date.now() + res.retry_after * 1000
        );
      }
      setSuccessMessage(
        "Verification email sent. Check your inbox or spam folder."
      );
    } catch (err) {
      const retry = err.response?.data?.retry_after;
      if (retry) {
        setResendCooldown(retry);
        localStorage.setItem(
          RESEND_EXPIRY_KEY,
          Date.now() + retry * 1000
        );
      }
      setErrorMessage(
        err.response?.data?.detail || "Could not resend verification email."
      );
    }
  };

  const openApplyDialog = async (app) => {
    try {
      // Refresh progress counts so the dialog shows up-to-date numbers
      const prog = await fetchAdminProgress();
      setAdminProgress(prog);
    } catch (err) {
      setErrorMessage("Could not refresh admin progress.");
    }
    setApplyDialog({ app, open: true });
  };

  const handleApply = async () => {
    try {
      await applyForAdmin(applyDialog.app);
      const prog = await fetchAdminProgress();
      setAdminProgress(prog);
      setSuccessMessage("Application submitted.");
    } catch (err) {
      setErrorMessage("Could not submit admin application.");
    }
    setApplyDialog({ app: "", open: false });
  };

  const roleGroupMap = {
    "Courses Admin": "CoursesAdminTeam",
    "Standard User": "StandardUser",
    "Community Admin": "CommunityAdminTeam",
    // Add more if needed in future
  };

  const instId = data.profile.institution
    ? typeof data.profile.institution === "object"
      ? data.profile.institution.id
      : data.profile.institution
    : null;
  const userCount = instStats?.users || 0;
  const instMaxAdmins =
    userCount <= 30 ? 3 : 3 + Math.floor((userCount - 30) / 20);
  const nextThreshold = instMaxAdmins <= 3 ? 50 : 30 + (instMaxAdmins - 2) * 20;
  const coursesAdmins = adminCounts?.courses_admins || 0;
  const communityAdmins = adminCounts?.community_admins || 0;
  const coursesApplicants = adminCounts?.courses_applicants || 0;
  const communityApplicants = adminCounts?.community_applicants || 0;
  const coursesLimit = coursesAdmins >= instMaxAdmins;
  const communityLimit = communityAdmins >= instMaxAdmins;
  const coursesNeeded = coursesLimit ? nextThreshold - userCount : 0;
  const communityNeeded = communityLimit ? nextThreshold - userCount : 0;
  const usersNeeded = Math.max(nextThreshold - userCount, 0);

  const COURSES_REQ = 10;
  const REVIEWS_REQ = 6;
  const THREADS_REQ = 5;
  const COMMENTS_REQ = 10;

  const progressColor = (count, req) => {
    if (count >= req) return "success";
    if (count > 0) return "warning";
    return "error";
  };

  const statusColor = (status) => {
    switch (status) {
      case "approved":
        return "success";
      case "rejected":
        return "error";
      case "reviewed":
        return "warning";
      default:
        return "default";
    }
  };

  const affiliationVerified =
    instId && data.profile.verified_institutions?.some((i) => i.id === instId);

  const coursesEligible =
    adminProgress &&
    (adminProgress.approved_submissions_count >= COURSES_REQ ||
      adminProgress.reviews_count >= REVIEWS_REQ) &&
    affiliationVerified;
  const communityEligible =
    adminProgress &&
    (adminProgress.threads_count >= THREADS_REQ ||
      adminProgress.comments_count >= COMMENTS_REQ) &&
    affiliationVerified;

  return (
    <Box maxWidth="md" mx="auto" p={4}>
      <Typography variant="h4" gutterBottom>
        Settings
      </Typography>

      <Card variant="outlined" sx={{ mb: 4, p: 0, pb: 3 }}>
        {/* Header */}
        <Box
          sx={{
            bgcolor: (theme) => theme.palette.action.hover,
            px: 2,
            py: 1.5,
            borderBottom: "1px solid",
            borderColor: "divider",
            borderTopLeftRadius: 1,
            borderTopRightRadius: 1,
          }}
        >
          <Typography variant="h6" fontWeight={600}>
            Display
          </Typography>
        </Box>

        <CardContent sx={{ px: 3, pt: 3, pb: 3 }}>
          <Stack spacing={2}>
            <Stack spacing={0.5}>
              <Typography
                variant="body2"
                fontWeight={600}
                color="text.secondary"
              >
                Theme Color
              </Typography>
              <RadioGroup
                row
                value={selectedScheme}
                onChange={(e) => {
                  const value = e.target.value;
                  setSchemeChanged(true);
                  setSelectedScheme(value);
                  if (value === "system") {
                    setMode("system");
                    setColorScheme("light");
                  } else if (value === "warm") {
                    setColorScheme("warm");
                  } else {
                    setMode(value);
                    setColorScheme(value);
                  }
                }}
              >
                {["light", "warm", "dark"].map((scheme) => (
                  <FormControlLabel
                    key={scheme}
                    value={scheme}
                    control={<Radio />}
                    label={`${scheme.charAt(0).toUpperCase() + scheme.slice(1)}${savedScheme === scheme ? " (current)" : ""}`}
                  />
                ))}
              </RadioGroup>
              {schemeChanged && (
                <Stack direction="row" spacing={1} mt={1}>
                  <FilledButton
                    size="small"
                    color="primary"
                    onClick={handleThemeSave}
                    disabled={selectedScheme === savedScheme}
                    text="Save"
                  />
                  <OutlinedButton
                    size="small"
                    color="primary"
                    onClick={handleThemeCancel}
                    text="Cancel"
                  />
                </Stack>
              )}
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      <Card variant="outlined" sx={{ mb: 4, p: 0, pb: 3 }}>
        {/* Header */}
        <Box
          sx={{
            bgcolor: (theme) => theme.palette.action.hover,
            px: 2,
            py: 1.5,
            borderBottom: "1px solid",
            borderColor: "divider",
            borderTopLeftRadius: 1,
            borderTopRightRadius: 1,
          }}
        >
          <Typography variant="h6" fontWeight={600}>
            Account Info
          </Typography>
        </Box>

        {/* Content */}
        <CardContent sx={{ px: 3, pt: 3, pb: 3 }}>
          <Stack spacing={2}>
            {/* Avatar */}
            <Stack spacing={0.5}>
              <Typography
                variant="body2"
                fontWeight={600}
                color="text.secondary"
              >
                Avatar
              </Typography>
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                flexWrap="wrap"
              >
                <Avatar
                  src={data.profile.avatar || undefined}
                  alt={data.user.username}
                  sx={{ width: 64, height: 64 }}
                >
                  {!data.profile.avatar && data.user.username?.[0]}
                </Avatar>
                {!data.profile.avatar ? (
                  <OutlinedButton
                    size="small"
                    color="primary"
                    text="Add Photo"
                    onClick={() => setAvatarDialogOpen(true)}
                    sx={{ textTransform: "none" }}
                  />
                ) : (
                  <>
                    <OutlinedButton
                      size="small"
                      color="primary"
                      text="Edit"
                      onClick={() => setAvatarDialogOpen(true)}
                      sx={{ textTransform: "none" }}
                    />
                    <OutlinedButton
                      size="small"
                      color="error"
                      text="Delete"
                      onClick={handleAvatarDelete}
                      sx={{ textTransform: "none" }}
                    />
                  </>
                )}
              </Stack>
            </Stack>
            <Divider sx={{ my: 2 }} />
            {/* Username */}
            <Stack spacing={0.5}>
              <Typography
                variant="body2"
                fontWeight={600}
                color="text.secondary"
              >
                Username
              </Typography>
              <Typography>{data.user.username}</Typography>
            </Stack>
            <Divider sx={{ my: 2 }} />
            {/* Email */}
            <Stack spacing={0.5}>
              <Typography
                variant="body2"
                fontWeight={600}
                color="text.secondary"
              >
                Email
              </Typography>
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                flexWrap="wrap"
              >
                <Typography>{data.user.email}</Typography>
                <IconOnlyChip
                  icon={<EditIcon fontSize="small" />}
                  onClick={() => setChangeEmailOpen(true)}
                  sx={{ ml: 1 }}
                />
                <TextOnlyChip
                  label={data.profile.email_verified ? "Verified" : "Unverified"}
                  color={data.profile.email_verified ? "success" : "error"}
                />
              </Stack>
              {!data.profile.email_verified && (
                <OutlinedButton
                  size="small"
                  color="primary"
                  onClick={handleResendVerification}
                  disabled={resendCooldown > 0}
                  text={
                    resendCooldown > 0
                      ? `Email sent! (${resendCooldown}s)`
                      : "Resend verification email"
                  }
                  sx={{ width: "fit-content", textTransform: "none", mt: 1 }}
                />
              )}
            </Stack>
            <Divider sx={{ my: 2 }} />
            {/* Password */}
            <Stack spacing={0.5}>
              <Typography
                variant="body2"
                fontWeight={600}
                color="text.secondary"
              >
                Password
              </Typography>
              <OutlinedButton
                size="small"
                color="primary"
                text="Change Password"
                onClick={() => setChangePasswordOpen(true)}
                disabled={!verified}
                sx={{
                  width: "fit-content",
                  textTransform: "none",
                  mt: 1,
                }}
              />
              {!verified && (
                <Alert severity="info" sx={{ mt: 1 }}>
                  Please verify your email before changing password.
                </Alert>
              )}
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      {/* Academic Info */}
      <Card id="institution" variant="outlined" sx={{ mb: 4, p: 0, pb: 3 }}>
        {/* Header */}
        <Box
          sx={{
            bgcolor: (theme) => theme.palette.action.hover,
            px: 2,
            py: 1.5,
            borderBottom: "1px solid",
            borderColor: "divider",
            borderTopLeftRadius: 1,
            borderTopRightRadius: 1,
          }}
        >
          <Typography variant="h6" fontWeight={600}>
            Affiliation
          </Typography>
        </Box>
        <CardContent sx={{ px: 3, pt: 3, pb: 3 }}>
          {/* Description */}
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Your selected institution determines the courses, community posts,
            and tutors you see.
          </Typography>
          <Divider sx={{ my: 2 }} />
          {/* Institution Search Field */}
          <Stack spacing={0.5}>
            <Typography variant="body2" fontWeight={600} color="text.secondary">
              Institution
            </Typography>

            {institutions.length > 0 && (
              <Autocomplete
                fullWidth
                size="small"
                componentsProps={{
                  clearIndicator: {
                    sx: {
                      p: 0,
                      width: "1.5rem",
                      height: "1.5rem",
                      border: 0,
                      bgcolor: "transparent",
                      "&:hover": { bgcolor: "transparent" },
                      "&:active": { bgcolor: "transparent" },
                    },
                  },
                  popupIndicator: {
                    sx: {
                      p: 0,
                      width: "1.5rem",
                      height: "1.5rem",
                      border: 0,
                      bgcolor: "transparent",
                      "&:hover": { bgcolor: "transparent" },
                      "&:active": { bgcolor: "transparent" },
                    },
                  },
                }}
                options={[...institutions].sort((a, b) =>
                  a.name.localeCompare(b.name),
                )}
                getOptionLabel={(option) => option.display_name}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                value={
                  institutions.find((inst) => {
                    const instId = data.profile.institution
                      ? typeof data.profile.institution === "object"
                        ? data.profile.institution.id
                        : data.profile.institution
                      : null;
                    return inst.id === instId;
                  }) || null
                }
                onChange={(e, newValue) => {
                  if (
                    newValue &&
                    (data.profile.institution &&
                    typeof data.profile.institution === "object"
                      ? data.profile.institution.id
                      : data.profile.institution) !== newValue.id
                  ) {
                    setPendingInstitution(newValue);
                    setConfirmInstitutionOpen(true);
                  }
                }}
                renderOption={(props, option) => (
                  <li {...props} key={option.id}>
                    {option.display_name}
                  </li>
                )}
                renderInput={(params) => (
                  <TextField {...params} placeholder="Select institution..." />
                )}
              />
            )}
            <Snackbar
              open={!!institutionAlert}
              autoHideDuration={4000}
              onClose={() => setInstitutionAlert("")}
              anchorOrigin={{ vertical: "top", horizontal: "center" }}
            >
              <Alert
                onClose={() => setInstitutionAlert("")}
                severity="info"
                sx={{ width: "100%" }}
              >
                {institutionAlert}
              </Alert>
            </Snackbar>
          </Stack>
        </CardContent>
      </Card>

      {/* Courses */}
      <Card variant="outlined" sx={{ mb: 4, p: 0, pb: 3 }}>
        {/* Header */}
        <Box
          sx={{
            bgcolor: (theme) => theme.palette.action.hover,
            px: 2,
            py: 1.5,
            borderBottom: "1px solid",
            borderColor: "divider",
            borderTopLeftRadius: 1,
            borderTopRightRadius: 1,
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <ClassRoundedIcon fontSize="small" color="action" />
            <Typography variant="h6" fontWeight={600}>
              Courses Profile
            </Typography>
          </Stack>
        </Box>
        <CardContent sx={{ px: 3, pt: 3, pb: 3 }}>
          {/* Description */}
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            You are always anonymous in the courses app.
          </Typography>
          <Divider sx={{ my: 2 }} />
          {/* Courses App */}
          <Stack spacing={0.5}>
            <Typography variant="body2" fontWeight={600} color="text.secondary">
              Current Role
            </Typography>
            <Typography>
              {data.profile.role_courses || "Standard User"}
            </Typography>
            {user?.is_superuser && (
              <Typography variant="caption" color="text.secondary">
                {coursesAdmins} admins, {coursesApplicants} applicants,{" "}
                {usersNeeded} more users until next admin slot
              </Typography>
            )}
            {data.profile.role_courses !== "Courses Admin" &&
              (adminProgress?.courses_status ? (
                <>
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    color="text.secondary"
                  >
                    Admin application status
                  </Typography>
                  <TextOnlyChip
                    sx={{
                      mt: 1,
                      alignSelf: "flex-start",
                      width: "fit-content",
                    }}
                    label={`${adminProgress.courses_status.charAt(0).toUpperCase()}${adminProgress.courses_status.slice(1)}`}
                    color={statusColor(adminProgress.courses_status)}
                    onClick={
                      adminProgress.courses_status === "rejected"
                        ? () =>
                            setAdminRejectAlert(
                              adminProgress.courses_rejected_reason,
                            )
                        : undefined
                    }
                  />
                </>
              ) : (
                <Tooltip
                  title={
                    coursesLimit
                      ? `Need ${coursesNeeded} more users to add another admin`
                      : ""
                  }
                  disableHoverListener={!coursesLimit}
                  describeChild
                >
                  <span>
                    <OutlinedButton
                      size="small"
                      color="primary"
                      disabled={coursesLimit}
                      onClick={() => openApplyDialog("courses")}
                      text="Become an Admin"
                      sx={{ mt: 1 }}
                    />
                  </span>
                </Tooltip>
              ))}
          </Stack>

          <Divider sx={{ my: 2 }} />

          <Stack spacing={0.5}>
            <Typography variant="body2" fontWeight={600} color="text.secondary">
              Permissions Matrix
            </Typography>
            <PermissionsMatrix
              permissions={coursePerms}
              currentRoleGroup={
                roleGroupMap[data.profile.role_courses] || "StandardUser"
              }
              appLabel="courses"
            />
          </Stack>
        </CardContent>
      </Card>

      {/* Community */}
      <Card variant="outlined" sx={{ mb: 4, p: 0, pb: 3 }}>
        {/* Header */}
        <Box
          sx={{
            bgcolor: (theme) => theme.palette.action.hover,
            px: 2,
            py: 1.5,
            borderBottom: "1px solid",
            borderColor: "divider",
            borderTopLeftRadius: 1,
            borderTopRightRadius: 1,
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <ForumRoundedIcon fontSize="small" color="action" />
            <Typography variant="h6" fontWeight={600}>
              Community Profile
            </Typography>
          </Stack>
        </Box>
        <CardContent sx={{ px: 3, pt: 3, pb: 3 }}>
          {/* Description */}
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            This name is visible in community discussions.
          </Typography>
          <Divider sx={{ my: 2 }} />
          {/* Community Profile Photo */}
          <Stack spacing={0.5}>
            <Typography variant="body2" fontWeight={600} color="text.secondary">
              Community Profile Photo
            </Typography>
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{ mb: 1 }}
            >
              <Typography variant="body2" color="text.secondary">
                Use account profile photo
              </Typography>
              <Switch
                size="small"
                checked={useAccountAvatar}
                onChange={(e) => updateUseAccountAvatar(e.target.checked)}
              />
            </Stack>
            {useAccountAvatar ? (
              <Avatar
                src={data.profile.avatar || undefined}
                alt={data.user.username}
                sx={{ width: 64, height: 64 }}
              >
                {!data.profile.avatar && data.user.username?.[0]}
              </Avatar>
            ) : (
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                flexWrap="wrap"
              >
                <Avatar
                  src={data.profile.community_avatar || undefined}
                  alt={data.user.username}
                  sx={{ width: 64, height: 64 }}
                >
                  {!data.profile.community_avatar && data.user.username?.[0]}
                </Avatar>
                {!data.profile.community_avatar ? (
                  <OutlinedButton
                    size="small"
                    color="primary"
                    text="Add Photo"
                    onClick={() => setCommunityAvatarDialogOpen(true)}
                    sx={{ textTransform: "none" }}
                  />
                ) : (
                  <>
                    <OutlinedButton
                      size="small"
                      color="primary"
                      text="Edit"
                      onClick={() => setCommunityAvatarDialogOpen(true)}
                      sx={{ textTransform: "none" }}
                    />
                    <OutlinedButton
                      size="small"
                      color="error"
                      text="Delete"
                      onClick={handleCommunityAvatarDelete}
                      sx={{ textTransform: "none" }}
                    />
                  </>
                )}
              </Stack>
            )}
          </Stack>
          <Divider sx={{ my: 2 }} />
          {/* Courses App */}
          <Stack spacing={0.5}>
            <Typography variant="body2" fontWeight={600} color="text.secondary">
              Community Username
            </Typography>

            {/* Toggle */}
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{ mb: 1 }}
            >
              <Typography variant="body2" color="text.secondary">
                Use sign-in username
              </Typography>
              <Switch
                size="small"
                checked={useDefaultCommunityUsername}
                onChange={(e) => {
                  const checked = e.target.checked;
                  setUseDefaultCommunityUsername(checked);
                  setCommunityUsernameError("");
                  setCommunityUsernameHelper("");

                  if (checked) {
                    const payload = {
                      profile: { community_username: data.user.username },
                    };
                    api.put("/users/profile/", payload).then(async () => {
                      const updated = await api.get("/users/profile/");
                      setData(updated.data);
                      setUseDefaultCommunityUsername(true);
                      setCommunityUsernameHelper("Using sign-in username.");
                      setCommunityUsernameError("");
                      setEditing((prev) => ({
                        ...prev,
                        community_username: false,
                      }));
                    });
                  } else {
                    startEditing("community_username");
                    setEditedValues((prev) => ({
                      ...prev,
                      community_username:
                        data.profile.community_username || data.user.username,
                    }));
                    setCommunityUsernameHelper(
                      "Customize your community username.",
                    );
                  }
                }}
              />
            </Stack>

            {/* Display/edit */}
            {useDefaultCommunityUsername ? (
              <Stack direction="row" spacing={1} alignItems="center">
                <TextField
                  size="small"
                  value={data.user.username}
                  disabled
                  fullWidth
                  sx={{ maxWidth: 300 }}
                />
                <IconOnlyChip
                  icon={
                    <EditIcon
                      fontSize="small"
                      sx={{ color: "text.disabled" }}
                    />
                  }
                  disabled
                  sx={{ ml: 1 }}
                />
              </Stack>
            ) : (
              <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                sx={{ mt: 1 }}
              >
                {editing.community_username ? (
                  <>
                    <Box
                      sx={{
                        position: "relative",
                        display: "inline-block",
                        maxWidth: 300,
                      }}
                    >
                      <TextField
                        size="small"
                        fullWidth
                        value={editedValues.community_username || ""}
                        onChange={(e) => {
                          const val = e.target.value;
                          handleChange("community_username", val);

                          if (
                            val ===
                            (data.profile.community_username ||
                              data.user.username)
                          ) {
                            setCommunityUsernameHelper(
                              "This is your current community username.",
                            );
                            setCommunityUsernameError("");
                          } else if (val === data.user.username) {
                            setCommunityUsernameHelper(
                              "This is your sign-in username.",
                            );
                            setCommunityUsernameError("");
                          } else {
                            setCommunityUsernameHelper(
                              "Username changed. Click save to update.",
                            );
                            setCommunityUsernameError("");
                          }
                        }}
                        error={!!communityUsernameError}
                        helperText={
                          communityUsernameError || communityUsernameHelper
                        }
                      />

                      <IconOnlyChip
                        sx={{
                          position: "absolute",
                          right: -32,
                          top: "0px",
                        }}
                        icon={<SaveIcon fontSize="small" />}
                        onClick={async () => {
                          const value = editedValues.community_username?.trim();
                          setCommunityUsernameError("");
                          setCommunityUsernameHelper("");
                          setCommunityUsernameAlert("");

                          if (!value) {
                            setCommunityUsernameError(
                              "Username cannot be empty.",
                            );
                            return;
                          }

                          if (value === data.user.username) {
                            await saveField("community_username");
                            setUseDefaultCommunityUsername(true);
                            setCommunityUsernameHelper(
                              "Using sign-in username.",
                            );
                            setCommunityUsernameAlert(
                              "Community username updated to sign-in username.",
                            );
                            return;
                          }

                          try {
                            await saveField("community_username");
                            setCommunityUsernameHelper(
                              "Community username updated.",
                            );
                            setCommunityUsernameAlert(
                              "Successfully updated your community username.",
                            );
                          } catch (err) {
                            const msg =
                              err?.response?.data?.profile?.community_username;
                            if (msg) setCommunityUsernameError(msg);
                            setErrorMessage("Failed to update username.");
                          }
                        }}
                      />
                    </Box>
                  </>
                ) : (
                  <>
                    <Typography>
                      {data.profile.community_username || "—"}
                    </Typography>
                    <IconOnlyChip
                      icon={<EditIcon fontSize="small" />}
                      onClick={() => startEditing("community_username")}
                    />
                  </>
                )}
              </Stack>
            )}
            <Snackbar
              open={!!communityUsernameAlert}
              autoHideDuration={4000}
              onClose={() => setCommunityUsernameAlert("")}
              anchorOrigin={{ vertical: "top", horizontal: "center" }}
            >
              <Alert
                onClose={() => setCommunityUsernameAlert("")}
                severity="success"
                variant="outlined"
                sx={{ width: "100%" }}
              >
                {communityUsernameAlert}
              </Alert>
            </Snackbar>
          </Stack>

          <Divider sx={{ my: 2 }} />
          {/* Current Role */}
          <Stack spacing={0.5} mt={2}>
            <Typography variant="body2" fontWeight={600} color="text.secondary">
              Current Role
            </Typography>
            <Typography>
              {data.profile.role_community || "Standard User"}
            </Typography>
            {user?.is_superuser && (
              <Typography variant="caption" color="text.secondary">
                {communityAdmins} admins, {communityApplicants} applicants,{" "}
                {usersNeeded} more users until next admin slot
              </Typography>
            )}
            {data.profile.role_community !== "Community Admin" &&
              (adminProgress?.community_status ? (
                <>
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    color="text.secondary"
                  >
                    Admin application status
                  </Typography>
                  <TextOnlyChip
                    sx={{
                      mt: 1,
                      alignSelf: "flex-start",
                      width: "fit-content",
                    }}
                    label={`${adminProgress.community_status.charAt(0).toUpperCase()}${adminProgress.community_status.slice(1)}`}
                    color={statusColor(adminProgress.community_status)}
                    onClick={
                      adminProgress.community_status === "rejected"
                        ? () =>
                            setAdminRejectAlert(
                              adminProgress.community_rejected_reason,
                            )
                        : undefined
                    }
                  />
                </>
              ) : (
                <Tooltip
                  title={
                    communityLimit
                      ? `Need ${communityNeeded} more users to add another admin`
                      : ""
                  }
                  disableHoverListener={!communityLimit}
                  describeChild
                >
                  <span>
                    <OutlinedButton
                      size="small"
                      color="primary"
                      disabled={communityLimit}
                      onClick={() => openApplyDialog("community")}
                      text="Become an Admin"
                      sx={{ mt: 1 }}
                    />
                  </span>
                </Tooltip>
              ))}
          </Stack>

          <Divider sx={{ my: 2 }} />

          <Stack spacing={0.5}>
            <Typography variant="body2" fontWeight={600} color="text.secondary">
              Permissions Matrix
            </Typography>
            <PermissionsMatrix
              permissions={communityPerms}
              currentRoleGroup={
                roleGroupMap[data.profile.role_community] || "StandardUser"
              }
              appLabel="community"
            />
          </Stack>
        </CardContent>
      </Card>

      <Snackbar
        open={!!adminRejectAlert}
        autoHideDuration={4000}
        onClose={() => setAdminRejectAlert("")}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setAdminRejectAlert("")}
          severity="error"
          variant="outlined"
          sx={{ width: "100%" }}
        >
          {adminRejectAlert}
        </Alert>
      </Snackbar>
      <Snackbar
        open={!!errorMessage}
        autoHideDuration={4000}
        onClose={() => setErrorMessage("")}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setErrorMessage("")}
          severity="error"
          sx={{ width: "100%" }}
        >
          {errorMessage}
        </Alert>
      </Snackbar>
      <Snackbar
        open={!!successMessage}
        autoHideDuration={4000}
        onClose={() => setSuccessMessage("")}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSuccessMessage("")}
          severity="success"
          sx={{ width: "100%" }}
        >
          {successMessage}
        </Alert>
      </Snackbar>

      {/* Tutoring */}
      <Card variant="outlined" sx={{ mb: 4, p: 0, pb: 3 }}>
        {/* Header */}
        <Box
          sx={{
            bgcolor: (theme) => theme.palette.action.hover,
            px: 2,
            py: 1.5,
            borderBottom: "1px solid",
            borderColor: "divider",
            borderTopLeftRadius: 1,
            borderTopRightRadius: 1,
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <SchoolRoundedIcon fontSize="small" color="action" />
            <Typography variant="h6" fontWeight={600}>
              Tutoring Profile
            </Typography>
          </Stack>
        </Box>
        <CardContent sx={{ px: 3, pt: 3, pb: 3 }}>
          {/* Description */}
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            You can have two profiles in tutoring. Manage them directly on those
            pages.
          </Typography>
          <Divider sx={{ my: 2 }} />

          <Stack spacing={0.5} mt={2}>
            <Typography variant="body2" fontWeight={600} color="text.secondary">
              Full Name
            </Typography>
            <Typography>{data.profile.full_name || "—"}</Typography>
          </Stack>

          <Stack spacing={0.5} mt={2}>
            <OutlinedButton
              fullWidth
              color="primary"
              to="/tutoring/tutor-profile/"
              text="Manage Tutor Profile"
            />
            <OutlinedButton
              fullWidth
              color="primary"
              to="/tutoring/student-profile/"
              text="Manage Student Profile"
            />
          </Stack>
        </CardContent>
      </Card>

      <Divider sx={{ my: 4 }} />

      {/* Notifications */}
      <Typography variant="h6" gutterBottom>
        Notification Preferences
      </Typography>
      {[
        "Email me when someone replies to my thread",
        "Email me when someone books a session",
        "Email me when I receive a review",
      ].map((label, idx) => (
        <Box
          key={idx}
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          py={1}
        >
          <Typography>{label}</Typography>
          <OutlinedButton size="small" color="primary" text="Manage" />
        </Box>
      ))}

      <ChangePasswordDialog
        open={changePasswordOpen}
        onClose={() => setChangePasswordOpen(false)}
        verified={verified}
      />
      <ChangeEmailDialog
        open={changeEmailOpen}
        onClose={() => setChangeEmailOpen(false)}
        onSuccess={async (msg) => {
          await refreshUserInfo();
          setSuccessMessage(msg);
        }}
      />

      <AvatarCropDialog
        open={avatarDialogOpen}
        onClose={() => setAvatarDialogOpen(false)}
        onSave={handleAvatarSave}
      />
      <AvatarCropDialog
        open={communityAvatarDialogOpen}
        onClose={() => setCommunityAvatarDialogOpen(false)}
        onSave={handleCommunityAvatarSave}
      />

      <Dialog
        open={confirmInstitutionOpen}
        onClose={() => setConfirmInstitutionOpen(false)}
      >
        <DialogTitle>Confirm Institution Change</DialogTitle>
        <DialogContent>
          <Typography>
            Change institution from{" "}
            {data.profile.institution
              ? typeof data.profile.institution === "object"
                ? data.profile.institution.display_name
                : institutions.find((i) => i.id === data.profile.institution)
                    ?.display_name || ""
              : ""}{" "}
            to {pendingInstitution?.display_name}?
          </Typography>
        </DialogContent>
        <DialogActions>
          <OutlinedButton
            onClick={() => {
              setConfirmInstitutionOpen(false);
              setPendingInstitution(null);
            }}
            text="Cancel"
            size="small"
            color="primary"
          />
          <FilledButton
            onClick={confirmInstitutionChange}
            text="Confirm"
            size="small"
            color="primary"
          />
        </DialogActions>
      </Dialog>

      <Dialog
        open={applyDialog.open}
        onClose={() => setApplyDialog({ app: "", open: false })}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Admin Application</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            To qualify, complete <strong>one</strong> of the following
            contribution goals:
          </Typography>
          <Box component="ul" sx={{ pl: 3, "& li": { mb: 1 } }}>
            {applyDialog.app === "courses" && (
              <>
                <li>
                  <Typography variant="body2" component="span">
                    Make {COURSES_REQ} total approved submissions on the courses
                    page (department, course, and professor)
                  </Typography>
                  <TextOnlyChip
                    sx={{ ml: 1 }}
                    label={`${adminProgress?.approved_submissions_count || 0} of ${COURSES_REQ} approved`}
                    color={progressColor(
                      adminProgress?.approved_submissions_count || 0,
                      COURSES_REQ,
                    )}
                  />
                </li>
                <li>
                  <Typography variant="body2" component="span">
                    Submit {REVIEWS_REQ} high-quality course reviews
                  </Typography>
                  <TextOnlyChip
                    sx={{ ml: 1 }}
                    label={`${adminProgress?.reviews_count || 0} of ${REVIEWS_REQ} submitted`}
                    color={progressColor(
                      adminProgress?.reviews_count || 0,
                      REVIEWS_REQ,
                    )}
                  />
                </li>
              </>
            )}
            {applyDialog.app === "community" && (
              <>
                <li>
                  <Typography variant="body2" component="span">
                    Make {THREADS_REQ} high-quality posts
                  </Typography>
                  <TextOnlyChip
                    sx={{ ml: 1 }}
                    label={`${adminProgress?.threads_count || 0} of ${THREADS_REQ} posted`}
                    color={progressColor(
                      adminProgress?.threads_count || 0,
                      THREADS_REQ,
                    )}
                  />
                </li>
                <li>
                  <Typography variant="body2" component="span">
                    Submit {COMMENTS_REQ} high-quality replies
                  </Typography>
                  <TextOnlyChip
                    sx={{ ml: 1 }}
                    label={`${adminProgress?.comments_count || 0} of ${COMMENTS_REQ} submitted`}
                    color={progressColor(
                      adminProgress?.comments_count || 0,
                      COMMENTS_REQ,
                    )}
                  />
                </li>
              </>
            )}
          </Box>
          <Divider sx={{ my: 1 }} />
          <Typography
            variant="body2"
            sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
          >
            Affiliation verification required
            <TextOnlyChip
              label={affiliationVerified ? "Verified" : "Unverified"}
              color={affiliationVerified ? "success" : "default"}
            />
          </Typography>
        </DialogContent>
        <DialogActions>
          <OutlinedButton
            onClick={() => setApplyDialog({ app: "", open: false })}
            text="Cancel"
            size="small"
            color="primary"
          />
          <FilledButton
            onClick={handleApply}
            disabled={
              applyDialog.app === "courses"
                ? !coursesEligible
                : !communityEligible
            }
            text="Apply"
            size="small"
            color="primary"
          />
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SettingsPage;
